import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ImperativePanelGroupHandle } from 'react-resizable-panels';
import { useAppSelector } from '../../../app/hooks';
import { selectUserProfile } from '../../../selectors/user';
import { resolvePaneUserKey } from '../../../utils/local-storage';
import {
  LayoutRegion,
  useLayoutPreference,
} from '../../Layout/LayoutPreferenceContext';

interface UseStackLayoutArgs {
  /** Which stack this is — 'left' (Transcript / AI Copilot) or 'right'. */
  region: LayoutRegion;
  /** Read this stack's saved [top, bottom] split, or null if none. */
  getSaved: (userKey: string) => [number, number] | null;
  /** Persist this stack's [top, bottom] split. */
  saveSaved: (userKey: string, sizes: [number, number]) => void;
  /** The old react-resizable-panels autoSaveId key to clean up once. */
  staleAutoSaveKey: string;
  /** Apply the saved split on mount (both stacks do, when a clean split exists). */
  restoreOnMount: boolean;
  /** Per-card clamp — only a split with both cards in [min, max] is "clean". */
  min: number;
  max: number;
}

/**
 * Custom persistence + live-apply for one vertical card stack — the vertical
 * twin of usePaneLayout's horizontal wiring. Each stack has a SINGLE source of
 * truth (its dedicated `hitl-layout-height-*` key), so a manual drag and an
 * explicit dialog "Apply" persist to the same place. The old PanelGroup
 * `autoSaveId` store is retired (and cleaned up) to avoid a second, diverging
 * store.
 *
 * Only a "clean" split — both cards within [min, max], i.e. neither collapsed —
 * is treated as a height preference: a collapsed state isn't persisted and isn't
 * restored. `savedOnMount` is returned so a caller can seed collapse state from
 * it (the right stack starts POS Cart expanded only when a saved split exists).
 */
const useStackLayout = ({
  region,
  getSaved,
  saveSaved,
  staleAutoSaveKey,
  restoreOnMount,
  min,
  max,
}: UseStackLayoutArgs) => {
  const userProfile = useAppSelector(selectUserProfile);
  const userKey = resolvePaneUserKey(userProfile);
  const groupRef = useRef<ImperativePanelGroupHandle>(null);
  const { reportLiveSizes, registerPaneSetter } = useLayoutPreference();

  const inRange = useCallback(
    (sizes: number[]) => sizes.every((size) => size >= min && size <= max),
    [min, max]
  );

  // Read the saved split once, on mount. getSaved already validates it sums to
  // 100; here we additionally require both cards to be in range, so a stored
  // collapsed layout isn't mistaken for a height preference.
  const [savedOnMount] = useState<[number, number] | null>(() => {
    const saved = getSaved(userKey);
    return saved && inRange(saved) ? saved : null;
  });

  // Let the Layout preference dialog resize this stack live while it's mounted;
  // clear the setter on unmount. Guard on panel count so a 2-size apply can't
  // hit a 1-panel group (right stack with the POS Cart flag off).
  useEffect(() => {
    registerPaneSetter(region, (sizes) => {
      const group = groupRef.current;
      if (group && group.getLayout().length === sizes.length) {
        group.setLayout(sizes);
      }
    });
    return () => registerPaneSetter(region, null);
  }, [region, registerPaneSetter]);

  // One-time cleanup of the old autoSaveId store now that this stack persists
  // via its own dedicated key.
  useEffect(() => {
    try {
      localStorage.removeItem(staleAutoSaveKey);
    } catch {
      /* storage unavailable — nothing to clean up */
    }
  }, [staleAutoSaveKey]);

  // Persist the split when the agent finishes dragging the divider. Only a clean
  // split is saved — dragging a card to collapse is handled by collapse-sync,
  // not stored as a height preference.
  const persistOnDragEnd = useCallback(
    (isDragging: boolean) => {
      if (isDragging) return;
      const sizes = groupRef.current?.getLayout();
      if (sizes && sizes.length === 2 && inRange(sizes)) {
        saveSaved(userKey, [sizes[0], sizes[1]]);
      }
    },
    [userKey, saveSaved, inRange]
  );

  // Apply the saved split once, on mount, before paint (no flash of default).
  useLayoutEffect(() => {
    if (!restoreOnMount || !savedOnMount) return;
    const group = groupRef.current;
    if (group && group.getLayout().length === savedOnMount.length) {
      group.setLayout(savedOnMount);
    }
    // Mount-only: restore the saved split once on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportSizes = useCallback(
    (sizes: number[]) => reportLiveSizes(region, sizes),
    [region, reportLiveSizes]
  );

  return { groupRef, reportSizes, persistOnDragEnd, savedOnMount };
};

export default useStackLayout;
